/* import __COLOCATED_TEMPLATE__ from './addon-card.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';

interface FeatureText {
  text: string;
  tooltip?: string;
}

interface Args {
  loading: boolean;
  addonIsSelected: boolean;
  unitPrice?: number;
  preventCheckboxDefault?: boolean;
  selectedUnitsQuantity?: number;
  toggleAddonSelectedState: () => void;
  upgradeFeatures?: Array<FeatureText>;
  upgradeLabel: string;
  upgradeDescription: string;
  descriptionLabel?: string;
  upgradeTooltipText?: string;
  upgradeSubcopy?: string;
  upgradeSubcopyList?: Array<FeatureText>;
  numberOfFeatureColumns?: number;
  showPricingArticle?: () => void;
  showEditIcon?: boolean;
  tooltipArticleId?: number;
  tooltipArticleTitle?: string;
  tooltipArticleAnalyticsObject?: string;
  analyticsPlace?: string;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class AddonCard extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: any;

  get icon(): InterfaceIconName {
    if (this.args.addonIsSelected) {
      return this.args.showEditIcon ? 'edit' : 'lead';
    } else {
      return 'new';
    }
  }

  get actionLabel(): string {
    return this.intl.t(this.actionLabelIntlString);
  }

  get actionLabelIntlString(): string {
    if (this.args.addonIsSelected) {
      return this.args.showEditIcon
        ? 'signup.teams.pricing5.annual-plans.upgrades.add-on-card.edit'
        : 'signup.teams.pricing5.annual-plans.upgrades.add-on-card.remove';
    } else {
      return 'signup.teams.pricing5.annual-plans.upgrades.add-on-card.add';
    }
  }

  get pricingInfoTitle(): string {
    return this.intl.t(
      'signup.teams.pricing5.annual-plans.upgrades.add-on-card.view-messages-pricing',
    );
  }

  private showArticleWithTracking(
    event: MouseEvent,
    objectName: string | undefined,
    articleId: number | undefined,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    if (objectName) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: objectName,
        place: this.args.analyticsPlace ?? '',
      });
    }

    window.Intercom('showArticle', articleId);
  }

  @action
  handlePricingArticleClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.args.showPricingArticle) {
      this.args.showPricingArticle();
    }
  }

  @action
  handleTooltipArticleClick(event: MouseEvent): void {
    this.showArticleWithTracking(
      event,
      this.args.tooltipArticleAnalyticsObject,
      this.args.tooltipArticleId,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::AddonCard': typeof AddonCard;
    'signup/teams/pricing5/components/addon-card': typeof AddonCard;
  }
}
